import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const TOAST = { position: "bottom-center", autoClose: 5000 };

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
};

const jsonHeaders = () => ({
  "X-CSRF-Token": csrfToken(),
  Accept: "application/json",
  "Content-Type": "application/json",
});

// With responseType: "blob" an error body arrives as a Blob, so the JSON has to be read
// back out before anything can inspect error.response.data.error.
export const readBlobError = async (error) => {
  const data = error && error.response && error.response.data;
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return error;

  try {
    error.response.data = JSON.parse(await data.text());
  } catch (e) {
    // Not JSON after all -- leave the Blob in place and let the caller fall through.
  }
  return error;
};

// Shared handling for the 402 every generation endpoint can return.
// Returns true when this was a billing block and the user has been told about it.
export const handleBillingError = (error) => {
  if (!error || !error.response || error.response.status !== 402) return false;

  const detail = (error.response.data && error.response.data.error) || {};
  const billingUrl = detail.billing_url || "/users/billing";

  toast.error(detail.message || "A subscription is required to generate certificates.", {
    ...TOAST,
    autoClose: 6000,
  });

  // A missing or paused subscription is only fixable on the billing page; a used-up
  // allowance is not, so leave the user where they are.
  if (detail.code === "no_subscription" || detail.code === "subscription_inactive") {
    window.setTimeout(() => {
      window.location.href = billingUrl;
    }, 1800);
  }

  return true;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const filenameFrom = (response, fallback) => {
  const disposition = response.headers && response.headers["content-disposition"];
  if (!disposition) return fallback;

  const match = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|([^;\n]*))/.exec(disposition);
  const name = match && (match[2] || match[3]);
  return name ? name.trim() : fallback;
};

const useBilling = (initial = null) => {
  const [billing, setBilling] = useState(initial);
  const [loading, setLoading] = useState(false);

  // Server-side redirect flow: we ask Rails for a PayPal approval URL and send the
  // browser there. No PayPal SDK, no credentials in the page.
  const subscribe = () => {
    setLoading(true);

    axios
      .post("/users/billing/subscribe", {}, { headers: jsonHeaders(), withCredentials: true })
      .then((response) => {
        const approveUrl = response.data && response.data.approve_url;
        if (approveUrl) {
          window.location.href = approveUrl;
          return; // navigating away: deliberately stay in the loading state
        }

        toast.error("PayPal did not return a checkout link. Please try again.", TOAST);
        setLoading(false);
      })
      .catch((error) => {
        const detail = (error.response && error.response.data && error.response.data.error) || {};
        toast.error(detail.message || "Could not start checkout. Please try again.", TOAST);
        setLoading(false);
      });
  };

  const cancel = () => {
    const confirmed = window.confirm(
      "Cancel your subscription? You keep access until the end of the period you have already paid for."
    );
    if (!confirmed) return;

    setLoading(true);

    axios
      .post("/users/billing/cancel", {}, { headers: jsonHeaders(), withCredentials: true })
      .then((response) => {
        setBilling(response.data);
        toast.success("Your subscription has been cancelled.", TOAST);
      })
      .catch((error) => {
        const detail = (error.response && error.response.data && error.response.data.error) || {};
        toast.error(detail.message || "Could not cancel the subscription. Please try again.", TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchStatus = () =>
    axios
      .get("/users/billing/status", { headers: { Accept: "application/json" }, withCredentials: true })
      .then((response) => {
        setBilling(response.data);
        return response.data;
      })
      .catch(() => null);

  return { billing, setBilling, loading, subscribe, cancel, fetchStatus };
};

export default useBilling;
